import { useEffect, useState, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Server,
  Users,
  Building2,
  Activity,
  AlertTriangle,
  ShoppingCart,
  BarChart2,
  CreditCard,
  CheckCircle2,
  Clock,
  RefreshCw,
  ExternalLink,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import type { AdminStats } from '../../types'
import { formatDate } from '../../utils/helpers'

// ── Stripe Connect onboarding guide (for ERP admins only) ────────────────────

function StripeOnboardingGuide({ erpId }: { erpId: number }) {
  const { t } = useTranslation()
  const [status, setStatus] = useState<'not_connected' | 'pending' | 'active' | null>(null)
  const [stripeEnabled, setStripeEnabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [onboarding, setOnboarding] = useState(false)

  const load = useCallback(() => {
    api
      .getStripeConfig(erpId)
      .then((cfg) => {
        setStripeEnabled(!!cfg?.enabled)
        if (cfg?.stripe_account_id) {
          return api.getStripeAccountStatus(erpId).then((s) => setStatus(s.status))
        } else {
          setStatus('not_connected')
        }
      })
      .catch(() => setStripeEnabled(false))
      .finally(() => setLoading(false))
  }, [erpId])

  useEffect(() => {
    load()
  }, [load])

  const handleOnboarding = async () => {
    setOnboarding(true)
    try {
      const res = await api.startStripeOnboarding(erpId)
      window.location.href = res.url
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      const msg = apiErr?.response?.data?.error ?? 'Erreur lors de la connexion Stripe'
      alert(msg)
    } finally {
      setOnboarding(false)
    }
  }

  if (loading || !stripeEnabled) return null

  const steps = [
    {
      done: true,
      label: t('admin.dashboard.stripeStep1', "Paiement en ligne activé par l'administrateur"),
    },
    {
      done: status === 'active',
      pending: status === 'pending',
      label: t(
        'admin.dashboard.stripeStep2',
        'Compte Stripe Express configuré (coordonnées bancaires, identité)'
      ),
      action: status !== 'active',
    },
    {
      done: status === 'active',
      label: t('admin.dashboard.stripeStep3', 'Vérification Stripe approuvée — paiements actifs'),
    },
  ]

  if (status === 'active') return null // All good — no need to show the guide

  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50 dark:bg-violet-900/20 dark:border-violet-700 p-5">
      <div className="flex items-start gap-3 mb-4">
        <CreditCard className="w-5 h-5 text-violet-600 dark:text-violet-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-violet-800 dark:text-violet-200">
            {t('admin.dashboard.stripeGuideTitle', 'Configurez le paiement en ligne')}
          </p>
          <p className="text-sm text-violet-600 dark:text-violet-400 mt-0.5">
            {t(
              'admin.dashboard.stripeGuideDesc',
              'Vos clients peuvent payer leurs factures directement depuis le portail. Complétez les étapes ci-dessous pour activer cette fonctionnalité.'
            )}
          </p>
        </div>
      </div>

      <ol className="space-y-2 mb-4">
        {steps.map((step, i) => (
          <li key={i} className="flex items-center gap-3">
            {step.done ? (
              <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
            ) : step.pending ? (
              <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
            ) : (
              <div className="w-4 h-4 rounded-full border-2 border-violet-400 flex-shrink-0" />
            )}
            <span
              className={`text-sm ${step.done ? 'text-gray-500 line-through' : 'text-gray-700 dark:text-gray-200'}`}
            >
              {step.label}
            </span>
          </li>
        ))}
      </ol>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={handleOnboarding}
          disabled={onboarding}
          className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
        >
          {onboarding ? (
            <RefreshCw size={14} className="animate-spin" />
          ) : (
            <ExternalLink size={14} />
          )}
          {status === 'not_connected'
            ? t('admin.dashboard.stripeStartOnboarding', 'Créer mon compte Stripe')
            : t('admin.dashboard.stripeResumeOnboarding', 'Reprendre la configuration')}
        </button>
      </div>
    </div>
  )
}

// ── Main dashboard ────────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const { t } = useTranslation()
  const location = useLocation()
  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') ?? '{}')
    } catch {
      return {}
    }
  })()
  const isErpAdmin = currentUser?.role === 'admin'
  const erpAdminErpId: number | null = currentUser?.erp_id ?? null

  const [stats, setStats] = useState<AdminStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Detect Stripe return/refresh from onboarding redirect
  const stripeReturn = new URLSearchParams(location.search).get('stripe_return') === '1'
  const stripeRefresh = new URLSearchParams(location.search).get('stripe_refresh') === '1'

  useEffect(() => {
    api
      .getAdminStats()
      .then(setStats)
      .catch(() => setError(t('common.error')))
      .finally(() => setLoading(false))
  }, [t])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return <div className="bg-red-50 text-red-700 p-4 rounded-lg">{error}</div>
  }

  const cards = [
    {
      name: t('admin.dashboard.erps'),
      value: stats?.erps ?? 0,
      icon: Server,
      color: 'from-blue-500 to-blue-600',
      href: '/admin/erps',
    },
    {
      name: t('admin.dashboard.suppliers'),
      value: stats?.customers ?? 0,
      icon: Building2,
      color: 'from-green-500 to-green-600',
      href: '/admin/customers',
    },
    {
      name: t('admin.dashboard.users'),
      value: stats?.users ?? 0,
      icon: Users,
      color: 'from-purple-500 to-purple-600',
      href: '/admin/users',
    },
    {
      name: t('admin.dashboard.activeUsers'),
      value: stats?.active_users ?? 0,
      icon: Activity,
      color: 'from-orange-500 to-orange-600',
      href: '/admin/users',
    },
  ]

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('admin.dashboard.title')}</h1>
        {stats?.version && (
          <span className="text-sm text-gray-400 font-medium">v{stats.version}</span>
        )}
      </div>

      {/* Schema version mismatch warning */}
      {stats?.schema_mismatch && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-xl p-4">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800">
              {t('admin.dashboard.schemaMismatchTitle')}
            </p>
            <p className="text-sm text-amber-700 mt-1">
              {t('admin.dashboard.schemaMismatchDesc', {
                db: stats.schema_version ?? '?',
                required: stats.schema_required ?? '?',
              })}
            </p>
          </div>
        </div>
      )}

      {/* Stripe Connect onboarding guide — shown to ERP admins only */}
      {isErpAdmin && erpAdminErpId && (
        <>
          {stripeReturn && (
            <div className="flex items-center gap-3 bg-green-50 border border-green-300 rounded-xl p-4">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-800">
                {t(
                  'admin.dashboard.stripeReturnSuccess',
                  "Vous avez bien complété les étapes d'onboarding. Stripe procède à la vérification de votre compte (quelques minutes à quelques heures)."
                )}
              </p>
            </div>
          )}
          {stripeRefresh && (
            <div className="flex items-center gap-3 bg-amber-50 border border-amber-300 rounded-xl p-4">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                {t(
                  'admin.dashboard.stripeRefreshWarning',
                  'Le lien d\'onboarding a expiré. Cliquez à nouveau sur "Reprendre la configuration" pour en générer un nouveau.'
                )}
              </p>
            </div>
          )}
          <StripeOnboardingGuide erpId={erpAdminErpId} />
        </>
      )}

      {/* Pending orders alert */}
      {(stats?.pending_orders_count ?? 0) > 0 && (
        <Link
          to="/admin/pending-orders"
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-orange-50 border border-orange-300 rounded-xl p-4 hover:bg-orange-100 transition-colors"
        >
          <div className="flex items-start gap-3">
            <ShoppingCart className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-orange-800">
                {t('admin.dashboard.pendingOrdersAlertTitle', {
                  count: stats!.pending_orders_count,
                })}
              </p>
              <p className="text-sm text-orange-700 mt-1">
                {t('admin.dashboard.pendingOrdersAlertDesc')}
              </p>
            </div>
          </div>
          <span className="text-sm font-medium text-orange-700 whitespace-nowrap">
            {t('admin.dashboard.pendingOrdersAlertAction')} →
          </span>
        </Link>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card) => {
          const Icon = card.icon
          return (
            <Link
              key={card.name}
              to={card.href}
              className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all p-6 border border-gray-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{card.name}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{card.value}</p>
                </div>
                <div
                  className={`w-14 h-14 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}
                >
                  <Icon className="w-7 h-7 text-white" />
                </div>
              </div>
            </Link>
          )
        })}
      </div>

      {/* Usage link */}
      <Link
        to="/admin/usage"
        className="flex items-center justify-between gap-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-xl p-4 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <BarChart2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
          <div>
            <p className="font-semibold text-indigo-800 dark:text-indigo-200">
              {t('adminUsage.title', 'Consommation')}
            </p>
            <p className="text-sm text-indigo-600 dark:text-indigo-400">
              {t('adminUsage.dashboardHint', 'Signatures, emails, tokens IA — vue mensuelle')}
            </p>
          </div>
        </div>
        <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300 whitespace-nowrap">
          {t('common.seeDetails', 'Voir le détail')} →
        </span>
      </Link>

      {/* Recent activity logs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {t('admin.dashboard.recentActivity')}
            </h3>
            <Link
              to="/admin/logs"
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              {t('admin.dashboard.viewAll')}
            </Link>
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {!stats?.recent_logs || stats.recent_logs.length === 0 ? (
            <p className="p-6 text-center text-gray-500">{t('admin.dashboard.noActivity')}</p>
          ) : (
            stats.recent_logs.map((log) => (
              <div key={log.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">
                      {log.user_email ?? `#${log.user_id}`}
                    </p>
                    <p className="text-sm text-gray-500">{log.action}</p>
                  </div>
                  <span className="text-sm text-gray-400">{formatDate(log.created_at)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
